// Own small-group WebRTC transport. Networking/auth is supplied by the screen.
// No public STUN/TURN provider is silently selected; deployment supplies config.
export default class MeetingPeerMesh {
  constructor({ read, send, rtcConfiguration, onPeers, onError }) {
    this.read = read; this.send = send; this.config = rtcConfiguration;
    this.onPeers = onPeers; this.onError = onError;
    this.peers = new Map(); this.tracks = { audio: null, video: null };
    this.closed = true; this.cursor = 0; this.epoch = null;
    this.timer = null; this.controller = null; this.outgoing = Promise.resolve();
  }
  async start() {
    if (!this.closed) return;
    this.closed = false; this.controller = new AbortController();
    try { await this.poll(); } catch (error) { this.fail(error); }
  }
  close() {
    this.closed = true; clearTimeout(this.timer); this.controller?.abort();
    for (const peer of this.peers.values()) {
      clearTimeout(peer.deadline);
      peer.pc.ontrack = null; peer.pc.onconnectionstatechange = null;
      peer.pc.close();
    }
    this.peers.clear(); this.onPeers([]);
    // Local device tracks belong to the preview component, not transport.
  }
  fail(error) {
    if (this.closed) return;
    this.close(); this.onError(error);
  }
  publish() {
    if (!this.closed) this.onPeers([...this.peers].map(([id, peer]) => ({
      id, name: peer.name, stream: peer.stream, state: peer.pc.connectionState,
    })));
  }
  async setTracks(tracks) {
    this.tracks = { audio: tracks.audio || null, video: tracks.video || null };
    try {
      await Promise.all([...this.peers.values()].flatMap((peer) =>
        peer.pc.getTransceivers().map((item) =>
          item.sender.replaceTrack(this.tracks[item.receiver.track.kind] || null))));
    } catch (error) { this.fail(error); }
  }
  createPeer(id, name) {
    const pc = new RTCPeerConnection(this.config);
    const peer = { pc, name, stream: new MediaStream(), connectionId: null };
    this.peers.set(id, peer);
    for (const kind of ["audio", "video"]) {
      pc.addTransceiver(kind, { direction: "sendrecv" });
    }
    pc.ontrack = ({ track }) => {
      if (this.closed || this.peers.get(id) !== peer) return;
      if (!peer.stream.getTracks().some((item) => item.id === track.id)) peer.stream.addTrack(track);
      this.publish();
    };
    pc.onconnectionstatechange = () => {
      if (this.closed || this.peers.get(id) !== peer) return;
      if (pc.connectionState === "connected") clearTimeout(peer.deadline);
      if (pc.connectionState === "failed") {
        this.fail(new Error("A media connection failed. Reconnect to the meeting.")); return;
      }
      this.publish();
    };
    peer.deadline = setTimeout(() => {
      if (!this.closed && this.peers.get(id) === peer && pc.connectionState !== "connected") {
        this.fail(new Error("A participant connection timed out. Reconnect to try again."));
      }
    }, 45000);
    return peer;
  }
  async syncTracks(peer) {
    await Promise.all(peer.pc.getTransceivers().map((item) =>
      item.sender.replaceTrack(this.tracks[item.receiver.track.kind] || null)));
  }
  async completeDescription(pc) {
    if (pc.iceGatheringState !== "complete") {
      await new Promise((resolve, reject) => {
        const signal = this.controller.signal;
        let timeout;
        const done = (error) => {
          clearTimeout(timeout); pc.removeEventListener("icegatheringstatechange", changed);
          signal.removeEventListener("abort", cancelled);
          error ? reject(error) : resolve();
        };
        const changed = () => { if (pc.iceGatheringState === "complete") done(); };
        const cancelled = () => done(new Error("Media session closed."));
        pc.addEventListener("icegatheringstatechange", changed);
        signal.addEventListener("abort", cancelled, { once: true });
        timeout = setTimeout(() => done(new Error("Network discovery timed out.")), 10000);
        if (signal.aborted) cancelled(); else changed();
      });
    }
    if (this.closed || !pc.localDescription) throw new Error("Media session closed.");
    return { type: pc.localDescription.type, sdp: pc.localDescription.sdp };
  }
  queueSignal(target, kind, value) {
    const payload = { request_id: crypto.randomUUID(), target, kind, data: JSON.stringify(value) };
    const work = this.outgoing.then(async () => {
      if (this.closed) throw new Error("Media session closed.");
      await this.send(payload, this.controller.signal);
    });
    this.outgoing = work.catch(() => {});
    return work;
  }
  async offer(id, peer) {
    peer.connectionId = crypto.randomUUID();
    await this.syncTracks(peer);
    await peer.pc.setLocalDescription(await peer.pc.createOffer());
    const description = await this.completeDescription(peer.pc);
    if (this.closed || this.peers.get(id) !== peer) return;
    await this.queueSignal(id, "offer", { connection_id: peer.connectionId, description });
  }
  async receive(message) {
    const peer = this.peers.get(message.sender);
    if (!peer) return;
    if (!["offer", "answer"].includes(message.kind)) throw new Error("Unsupported media signal.");
    const value = JSON.parse(message.data);
    if (typeof value.connection_id !== "string" || value.connection_id.length > 80
        || value.description?.type !== message.kind || typeof value.description.sdp !== "string"
        || value.description.sdp.length > 65000) throw new Error("Invalid media description.");
    if (message.kind === "offer") {
      // A single deterministic offerer prevents negotiation glare.
      if (!(message.sender < this.self)) throw new Error("Unexpected media offer.");
      if (peer.connectionId === value.connection_id) return;
      if (peer.connectionId) throw new Error("Participant restarted. Reconnect to the meeting.");
      peer.connectionId = value.connection_id;
      await peer.pc.setRemoteDescription(value.description);
      await this.syncTracks(peer);
      await peer.pc.setLocalDescription(await peer.pc.createAnswer());
      const description = await this.completeDescription(peer.pc);
      await this.queueSignal(message.sender, "answer", { connection_id: peer.connectionId, description });
    } else {
      if (peer.connectionId !== value.connection_id) return;
      if (!(this.self < message.sender)) throw new Error("Unexpected media answer.");
      if (peer.pc.signalingState === "have-local-offer") await peer.pc.setRemoteDescription(value.description);
    }
  }
  async poll() {
    if (this.closed) return;
    const data = await this.read(this.cursor, this.controller.signal);
    if (this.closed) return;
    if (!data || !Array.isArray(data.peers) || !Array.isArray(data.messages)
        || !Number.isSafeInteger(data.cursor) || data.cursor < this.cursor
        || typeof data.self !== "string" || typeof data.room_epoch !== "string") {
      throw new Error("Invalid meeting connection response.");
    }
    if (this.epoch && (this.epoch !== data.room_epoch || this.self !== data.self)) {
      throw new Error("Meeting access changed. Reopen your invitation.");
    }
    const rtc = data.rtc_configuration;
    if (rtc?.iceTransportPolicy !== "relay" || !Array.isArray(rtc.iceServers)
        || rtc.iceServers.length === 0 || !Number.isSafeInteger(data.relay_credentials_expires_at)
        || data.relay_credentials_expires_at * 1000 <= Date.now()) {
      throw new Error("Meeting relay configuration is unavailable.");
    }
    this.config = rtc;
    // Refresh credentials for future allocations, not a server cutoff of
    // established connections. Never fall back to direct/public ICE servers.
    for (const peer of this.peers.values()) peer.pc.setConfiguration(rtc);
    this.epoch = data.room_epoch; this.self = data.self;
    const present = new Set(data.peers.map((peer) => peer.id));
    if (!present.has(this.self)) throw new Error("Meeting admission is no longer valid.");
    for (const [id, peer] of this.peers) {
      if (!present.has(id)) {
        clearTimeout(peer.deadline); peer.pc.close(); this.peers.delete(id);
      }
    }
    const offers = [];
    for (const member of data.peers) {
      if (member.id === this.self) continue;
      if (!/^(host|[a-f0-9]{64})$/.test(member.id)) throw new Error("Invalid meeting participant.");
      let peer = this.peers.get(member.id);
      if (!peer) {
        peer = this.createPeer(member.id, member.display_name);
        if (this.self < member.id) offers.push(this.offer(member.id, peer));
      } else peer.name = member.display_name;
    }
    await Promise.all(offers);
    for (const message of data.messages) {
      if (this.closed) return;
      await this.receive(message);
    }
    if (this.closed) return;
    this.cursor = data.cursor; this.publish();
    this.timer = setTimeout(() => this.poll().catch((error) => this.fail(error)), 2000);
  }
}
